import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from './Modal';
import AccountInformation from '../Account/AccountInformation';
import SubscriptionPlan from '../Account/SubscriptionPlan';
import IdentityVerification from '../Account/IdentityVerification';
import PasswordSection from '../Account/PasswordSection';
import SubscriptionInvoices from '../Account/SubscriptionInvoices';
import DeactivateAccountSection from '../Account/DeactivateAccountSection';
import TwoFactorSection from '../Account/TwoFactorSection';
import Form from '../Form/Form';
import Button from '../Form/Button';
import { useAuth } from '../../context/AuthProvider';
import {
  faUser,
  faStar,
  faLock,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';
import './AccountModal.scss';

const SECTIONS = [
  {
    id: 'accountInformation',
    icon: faUser,
    label: 'Account',
    tint: '#2563eb',
    tintBg: '#eff6ff',
    Content: AccountInformation,
  },
  {
    id: 'subscriptionPlan',
    icon: faStar,
    label: 'Abonnement',
    tint: '#d97706',
    tintBg: '#fffbeb',
    Content: SubscriptionPlan,
  },
  {
    id: 'accountPassword',
    icon: faLock,
    label: 'Sécurité',
    tint: '#7c3aed',
    tintBg: '#f5f3ff',
    Content: PasswordSection,
  },
];

// Only these tabs have fields tied to the "accountModalForm" submit — the footer's
// Enregistrer button is meaningless (and hidden) while viewing the others.
const SAVE_BUTTON_TABS = new Set(['accountInformation', 'accountPassword']);

export default function AccountModal({ show, onClose, initialTab = 'accountInformation' }) {
  const { login, user } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [activeSection, setActiveSection] = useState(initialTab);

  // A menu switches between sections instead of dumping everything into one long
  // scroll — jump straight to whichever section the caller asked for (e.g. "Subscription
  // Plan" from the user menu) each time the modal is (re)opened.
  useEffect(() => {
    if (show) setActiveSection(initialTab);
  }, [show, initialTab]);

  const performSave = (data) => {
    setFormLoading(true);
    axios.put(`${API_BASE_URL}/api/auth/me/update`, data, { withCredentials: true })
      .then(() => {
        toast.success('Compte enregistré avec succès !');
        setPendingData(null);
        login();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Mot de passe incorrect ou erreur lors de l\'enregistrement.');
      })
      .finally(() => setFormLoading(false));
  };

  const handleSubmit = (data) => setPendingData(data);
  const handleVerifyConfirm = (verifyData) => performSave({ ...pendingData, currentPassword: verifyData.currentPassword });

  if (!user) return null;

  return (
    <>
      <Modal
        id="accountModal"
        title="Mon compte"
        show={show}
        onClose={onClose}
        icon={faUser}
        iconBg="#eff6ff"
        iconColor="#2563eb"
        classSize="modal-lg"
        dialogStyle={{ maxWidth: 680 }}
        noBodyPadding
        footer={
          <>
            <button type="button" className="modal-btn-secondary" onClick={onClose}>
              Fermer
            </button>
            {SAVE_BUTTON_TABS.has(activeSection) && (
              <Button
                isLoading={formLoading}
                loadingMessage="Enregistrement..."
                className="modal-btn-primary"
                type="submit"
                form="accountModalForm"
              >
                Enregistrer
              </Button>
            )}
          </>
        }
      >
        <div className="account-modal">
          <nav className="account-modal__nav">
            {SECTIONS.map(({ id, icon, label, tint, tintBg }) => (
              <button
                key={id}
                type="button"
                className={`account-modal__nav-item ${activeSection === id ? 'active' : ''}`}
                onClick={() => setActiveSection(id)}
              >
                <span className="account-modal__nav-icon" style={{ background: tintBg, color: tint }}>
                  <FontAwesomeIcon icon={icon} />
                </span>
                {label}
              </button>
            ))}
          </nav>

          <Form id="accountModalForm" onSubmit={handleSubmit} defaultValues={{ ...user, pharmacyId: 1 }}>
            {SECTIONS.map(({ id, Content }) => (
              <section
                key={id}
                id={id}
                className="account-modal__section"
                style={{ display: activeSection === id ? 'block' : 'none' }}
              >
                <Content user={user} />
                {id === 'subscriptionPlan' && (
                  <div className="mt-4">
                    <h6 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Factures</h6>
                    <SubscriptionInvoices />
                  </div>
                )}
                {id === 'accountPassword' && (
                  <>
                    <TwoFactorSection user={user} />
                    <DeactivateAccountSection user={user} />
                  </>
                )}
              </section>
            ))}
          </Form>
        </div>
      </Modal>

      <Modal
        id="verifyIdentityModalInAccount"
        title="Vérification d'identité"
        show={!!pendingData}
        onClose={() => setPendingData(null)}
        icon={faShieldHalved}
        iconBg="#fef2f2"
        iconColor="#dc2626"
        footer={
          <Button
            isLoading={formLoading}
            loadingMessage="Vérification…"
            className="btn btn-primary w-100"
            type="submit"
            form="verifyIdentityFormInAccount"
          >
            Confirmer
          </Button>
        }
      >
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 16px' }}>
          Confirmez votre mot de passe actuel pour enregistrer ces modifications.
        </p>
        <Form id="verifyIdentityFormInAccount" onSubmit={handleVerifyConfirm}>
          <IdentityVerification />
        </Form>
      </Modal>
    </>
  );
}
